import React, { useState } from "react";
import { X, Target, Loader2, TrendingUp, Percent, ArrowRight } from "lucide-react";
import { runOptimizer } from "@/lib/optimizerApi";
import { toast } from "sonner";

const fmt$ = (n) =>
  n == null ? "—" :
  `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const TabButton = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] rounded transition ${
      active
        ? "bg-[#A98CFF]/15 text-[#A98CFF] border border-[#A98CFF]/40"
        : "border border-white/10 text-white/55 hover:text-white"
    }`}
  >
    {children}
  </button>
);

/**
 * OptionStrat-style Optimizer modal.
 *
 * User enters a target price + target date; the backend scans 13 strategy
 * templates × 5 strike offsets × 3 widths (~180 candidates) and ranks them
 * by either projected P/L at the target OR probability of profit. Clicking
 * a result loads the strategy into the builder via `onPick`.
 */
const OptimizerModal = ({
  onClose, onPick, symbol, spot, iv,
  defaultTargetPrice, defaultTargetDate,
}) => {
  // Range mode toggle — when ON, "Target price" splits into Min + Max
  // and the optimizer scores P/L as the worst-case across the range.
  const initialTarget = defaultTargetPrice ?? (spot ? Math.round(spot * 1.05 * 100) / 100 : 100);
  const [rangeMode, setRangeMode] = useState(false);
  const [targetPrice, setTargetPrice] = useState(initialTarget);
  const [targetLow, setTargetLow] = useState(
    spot ? Math.round(spot * 1.02 * 100) / 100 : initialTarget * 0.97,
  );
  const [targetHigh, setTargetHigh] = useState(
    spot ? Math.round(spot * 1.08 * 100) / 100 : initialTarget * 1.03,
  );
  const [targetDate, setTargetDate] = useState(
    defaultTargetDate ??
      new Date(Date.now() + 45 * 86400000).toISOString().slice(0, 10),
  );
  const [tab, setTab] = useState("best_return");
  const [results, setResults] = useState(null);
  const [running, setRunning] = useState(false);

  const handleRun = async () => {
    if (!spot || !targetDate) {
      toast.error("Spot and date are required");
      return;
    }
    if (rangeMode) {
      if (!targetLow || !targetHigh || targetHigh <= targetLow) {
        toast.error("Min must be less than max");
        return;
      }
    } else if (!targetPrice) {
      toast.error("Target price is required");
      return;
    }
    setRunning(true);
    try {
      const payload = {
        symbol: symbol || null,
        spot,
        iv,
        target_price: rangeMode ? (targetLow + targetHigh) / 2 : targetPrice,
        target_date: targetDate,
        qty: 1,
        top_n: 10,
      };
      if (rangeMode) {
        payload.target_price_low = targetLow;
        payload.target_price_high = targetHigh;
      }
      const r = await runOptimizer(payload);
      setResults(r);
    } catch (err) {
      toast.error("Optimizer failed");
      console.error(err);
    } finally {
      setRunning(false);
    }
  };

  const list = results ? results[tab] || [] : [];
  const directionUp = results?.input?.direction_up;

  return (
    <div
      className="fixed inset-0 z-50 bg-[#050505]/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="optimizer-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-[#0a0a0c] border border-[#A98CFF]/30 rounded-2xl p-5 sm:p-6 w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#A98CFF] flex items-center gap-1.5">
              <Target className="w-3 h-3" /> Optimizer
            </div>
            <h3 className="text-lg font-medium tracking-tight mt-0.5">
              Find the best trade for your target
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="optimizer-close"
            className="p-1 text-white/55 hover:text-white border border-white/15 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Range toggle */}
        <div className="flex items-center gap-2 mb-2.5">
          <button
            type="button"
            onClick={() => setRangeMode((v) => !v)}
            data-testid="optimizer-range-toggle"
            className={
              "text-[10px] uppercase tracking-[0.18em] inline-flex items-center gap-1.5 px-2 py-1 rounded transition " +
              (rangeMode
                ? "bg-[#A98CFF]/15 text-[#A98CFF] border border-[#A98CFF]/45"
                : "border border-white/15 text-white/55 hover:text-white hover:border-white/30")
            }
          >
            {rangeMode ? "Range mode · ON" : "Single target · click for range"}
          </button>
          {rangeMode && (
            <span className="text-[10px] text-white/40 italic">
              Worst-case P/L across the range — your floor if spot lands anywhere inside.
            </span>
          )}
        </div>

        {/* Inputs */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-[12px] mb-4">
          {rangeMode ? (
            <div className="sm:col-span-1 grid grid-cols-2 gap-2">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.18em] text-white/45">
                  Min · spot ${spot?.toFixed?.(2)}
                </span>
                <input
                  type="number"
                  step="any"
                  value={targetLow}
                  onChange={(e) => setTargetLow(parseFloat(e.target.value) || 0)}
                  data-testid="optimizer-target-low"
                  className="mt-1 w-full bg-[#050505] border border-white/10 focus:border-[#A98CFF]/60 outline-none rounded-md px-2 py-2 num text-white"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.18em] text-white/45">
                  Max
                </span>
                <input
                  type="number"
                  step="any"
                  value={targetHigh}
                  onChange={(e) => setTargetHigh(parseFloat(e.target.value) || 0)}
                  data-testid="optimizer-target-high"
                  className="mt-1 w-full bg-[#050505] border border-white/10 focus:border-[#A98CFF]/60 outline-none rounded-md px-2 py-2 num text-white"
                />
              </label>
            </div>
          ) : (
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.18em] text-white/45">
                Target price · current ${spot?.toFixed?.(2)}
              </span>
              <input
                type="number"
                step="any"
                value={targetPrice}
                onChange={(e) => setTargetPrice(parseFloat(e.target.value) || 0)}
                data-testid="optimizer-target-price"
                className="mt-1 w-full bg-[#050505] border border-white/10 focus:border-[#A98CFF]/60 outline-none rounded-md px-3 py-2 num text-white"
              />
            </label>
          )}
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.18em] text-white/45">
              Target date
            </span>
            <input
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              data-testid="optimizer-target-date"
              className="mt-1 w-full bg-[#050505] border border-white/10 focus:border-[#A98CFF]/60 outline-none rounded-md px-3 py-2 num text-white"
            />
          </label>
          <div className="flex items-end">
            <button
              type="button"
              onClick={handleRun}
              disabled={running}
              data-testid="optimizer-run"
              className="w-full inline-flex items-center justify-center gap-1.5 bg-[#A98CFF] text-black hover:bg-[#C4B5FD] px-4 py-2 rounded-md text-[12.5px] font-medium disabled:opacity-50"
            >
              {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Target className="w-3.5 h-3.5" />}
              {running ? "Scanning…" : "Optimize"}
            </button>
          </div>
        </div>

        {/* Result header + tabs */}
        {results && (
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-[11px] text-white/55">
              Scanned <span className="num font-medium text-white">{results.scan_size}</span>{" "}
              candidates · direction{" "}
              <span className={directionUp ? "text-[#00C805]" : "text-[#FF3B30]"}>
                {directionUp ? "↑" : "↓"}
              </span>
              {results.input.is_range && results.input.target_price_low ? (
                <>
                  {" · range "}
                  <span className="num text-white">${Number(results.input.target_price_low).toFixed(2)}</span>
                  {" – "}
                  <span className="num text-white">${Number(results.input.target_price_high).toFixed(2)}</span>
                  {" · prob spot lands inside: "}
                </>
              ) : (
                " · prob of reaching target: "
              )}
              <span className="num font-medium text-[#A98CFF]">
                {results.input.prob_reaching_target}%
              </span>
            </div>
            <div className="flex gap-1.5">
              <TabButton
                active={tab === "best_return"}
                onClick={() => setTab("best_return")}
                testid="optimizer-tab-return"
              >
                <TrendingUp className="w-3 h-3 inline mr-1" /> Best return
              </TabButton>
              <TabButton
                active={tab === "best_chance"}
                onClick={() => setTab("best_chance")}
                testid="optimizer-tab-chance"
              >
                <Percent className="w-3 h-3 inline mr-1" /> Best chance
              </TabButton>
            </div>
          </div>
        )}

        {/* Results table */}
        <div className="flex-1 overflow-auto -mx-2">
          {!results && !running && (
            <div className="text-center text-white/40 text-[12px] py-12 italic">
              Set a target price + date, then hit Optimize.
            </div>
          )}
          {running && (
            <div className="text-center text-white/55 py-12">
              <Loader2 className="w-5 h-5 animate-spin inline" />
            </div>
          )}
          {!!list.length && (
            <table className="w-full text-[11px]">
              <thead className="text-white/40 uppercase tracking-[0.16em] text-[9px] sticky top-0 bg-[#0a0a0c]">
                <tr>
                  <th className="text-left px-2 py-1.5">Strategy</th>
                  <th className="text-right px-2 py-1.5">Strikes</th>
                  <th className="text-right px-2 py-1.5">Projected</th>
                  <th className="text-right px-2 py-1.5">Max P/L</th>
                  <th className="text-right px-2 py-1.5">POP</th>
                  <th className="text-right px-2 py-1.5">RR</th>
                  <th className="px-2 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((c, i) => (
                  <tr
                    key={`${c.template_id}-${c.strike_pct}-${c.width_pct}-${i}`}
                    className="border-t border-white/[0.06] hover:bg-white/[0.03]"
                  >
                    <td className="px-2 py-1.5 text-white">
                      {c.template_name}
                      <div className="text-[9px] uppercase tracking-[0.16em] text-white/35 mt-0.5">
                        {c.bias}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 num text-right text-white/65">
                      sp {c.strike_pct >= 0 ? "+" : ""}{c.strike_pct}% · w {c.width_pct}%
                    </td>
                    <td
                      className="px-2 py-1.5 num text-right font-medium"
                      style={{ color: c.projected_pnl >= 0 ? "#00C805" : "#FF3B30" }}
                    >
                      {fmt$(c.projected_pnl)}
                      <div
                        className="text-[9px]"
                        style={{ color: c.projected_pnl >= 0 ? "#00C805" : "#FF3B30" }}
                      >
                        {c.projected_return_pct >= 0 ? "+" : ""}
                        {c.projected_return_pct}%
                      </div>
                    </td>
                    <td className="px-2 py-1.5 num text-right text-white/65">
                      <div className="text-[#00C805]">{fmt$(c.max_profit)}</div>
                      <div className="text-[#FF3B30]">{fmt$(c.max_loss)}</div>
                    </td>
                    <td className="px-2 py-1.5 num text-right text-[#A98CFF]">{c.pop}%</td>
                    <td className="px-2 py-1.5 num text-right text-white/65">{c.rr_ratio}x</td>
                    <td className="px-2 py-1.5">
                      <button
                        type="button"
                        onClick={() => onPick(c)}
                        data-testid={`optimizer-pick-${i}`}
                        className="text-[10.5px] inline-flex items-center gap-1 text-[#A98CFF] hover:text-white px-2 py-0.5 rounded border border-[#A98CFF]/40 hover:border-white"
                      >
                        Load <ArrowRight className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default OptimizerModal;
